import L from 'leaflet'
import 'leaflet.markercluster'
import { SPIDERFY } from './mapConfig'

/**
 * PROTOTYPE — takes the spiderfy layout + reveal away from leaflet.markercluster.
 *
 * The plugin slides every spiderfied pin out from the cluster centre with one
 * shared CSS transition and tears the animation class off on a hard-coded 200ms
 * timer, so there is no supported way to stagger the pins or change the motion.
 * Here we:
 *
 *   1. supply our own `spiderfyShapePositions` (a faithful port of the plugin's
 *      spiral / circle maths, plus `startRadiusOffset`),
 *   2. replace `_animationSpiderfy` with a version that drops each pin straight
 *      onto its final slot and reveals it *in place* with a staggered scale-in +
 *      fade-in on the inner `.motif-marker-image` (the outer `.leaflet-marker-icon`
 *      keeps Leaflet's positioning transform), innermost pin first, and
 *   3. replace `_animationUnspiderfy` with the mirror image: an outermost-first
 *      staggered scale-out + fade-out, in place. A zoom-driven collapse still
 *      falls through to the stock (fast) method — the zoom transition waits on it.
 */

const TWO_PI = Math.PI * 2

// --- geometry -------------------------------------------------------------

const place = (center: L.Point, radius: number, angle: number): L.Point =>
  new L.Point(
    center.x + (radius + SPIDERFY.startRadiusOffset) * Math.cos(angle),
    center.y + (radius + SPIDERFY.startRadiusOffset) * Math.sin(angle),
  ).round()

/** Port of MarkerCluster._generatePointsCircle (used below the switchover count). */
const generateCircle = (count: number, center: L.Point): L.Point[] => {
  const circumference = SPIDERFY.distanceMultiplier * 25 * (2 + count)
  const legLength = Math.max(circumference / TWO_PI, 35)
  const angleStep = TWO_PI / count

  return Array.from({ length: count }, (_, i) => place(center, legLength, i * angleStep))
}

/**
 * Port of MarkerCluster._generatePointsSpiral (used at/above the switchover count).
 *
 * `legLength` is both the placement radius and the divisor for the angular step,
 * so it MUST start at the plugin's ~11 for the arc spacing to stay even. To lift
 * the innermost pins off the cluster badge we roll the spiral forward
 * `innerSkip` turns and drop those points, rather than pushing pins out radially
 * (a flat offset over-spaces the inner turns, since the angle step is still
 * derived from the smaller `legLength`).
 */
const generateSpiral = (count: number, center: L.Point): L.Point[] => {
  const total = count + SPIDERFY.innerSkip
  const separation = SPIDERFY.distanceMultiplier * 28
  const lengthFactor = SPIDERFY.distanceMultiplier * 5 * TWO_PI
  let legLength = SPIDERFY.distanceMultiplier * 11
  let angle = 0

  const res: L.Point[] = new Array(count)
  // higher index => closer to the centre; the innermost `innerSkip` are computed
  // (to advance the spiral) but never placed
  for (let i = total; i >= 0; i--) {
    if (i < count) {
      res[i] = place(center, legLength, angle)
    }
    angle += separation / legLength + i * 0.0005
    legLength += lengthFactor / angle
  }
  return res
}

export const spiderfyShapePositions = (count: number, centerPt: L.Point): L.Point[] =>
  count >= SPIDERFY.circleToSpiralAt
    ? generateSpiral(count, centerPt)
    : generateCircle(count, centerPt)

// --- staggered reveal / collapse ---------------------------------------

type SpiderMarker = L.Marker & {
  _spiderLeg?: L.Polyline
  _preSpiderfyLatlng?: L.LatLng
  /** Pixel-level move that doesn't touch `_latlng` (used to pin a pin mid-fade). */
  _setPos?: (point: L.Point) => void
}

type InternalGroup = L.MarkerClusterGroup & {
  _map: L.Map
  _featureGroup: L.FeatureGroup
  _ignoreMove: boolean
  _spiderfied: unknown
  options: { spiderLegPolylineOptions?: L.PolylineOptions }
  _forceLayout: () => void
  _animationStart: () => void
  _animationEnd: () => void
}

type InternalCluster = L.MarkerCluster & {
  _latlng: L.LatLng
  _group: InternalGroup
  getAllChildMarkers: (filter: null, ignoreDragged: boolean) => SpiderMarker[]
  setOpacity: (opacity: number) => void
}

/** Zoom animation payload the plugin threads through `unspiderfy`. */
type ZoomAnimDetails = { zoom: number; center: L.LatLng }

/** Slack added to the cascade timer so the last transition is fully done before cleanup. */
const TRANSITION_TAIL_MS = 60

/** The element we scale + fade; the outer icon keeps Leaflet's positioning transform. */
const innerImage = (marker: L.Marker): HTMLElement | null =>
  marker.getElement()?.querySelector<HTMLElement>('.motif-marker-image') ?? null

/**
 * Per-pin step in the cascade: `staggerMs`, squeezed down so the whole run
 * (first pin → last pin) never outlasts `maxSpreadMs` on a big cluster.
 */
const effectiveStagger = (count: number): number => {
  const lastIndex = count - 1
  return lastIndex > 0 ? Math.min(SPIDERFY.staggerMs, SPIDERFY.maxSpreadMs / lastIndex) : 0
}

const clearInlineReveal = (inner: HTMLElement | null) => {
  if (!inner) return
  inner.style.removeProperty('transition')
  inner.style.removeProperty('transition-delay')
  inner.style.removeProperty('transform')
  inner.style.removeProperty('opacity')
}

let installed = false

/** Call once, before any marker cluster group is created. Idempotent. */
export const installStaggeredSpiderfy = () => {
  if (installed) return
  installed = true

  const proto = L.MarkerCluster.prototype as unknown as {
    _animationSpiderfy(childMarkers: SpiderMarker[], positions: L.Point[]): void
    _animationUnspiderfy(zoomDetails?: ZoomAnimDetails): void
  }
  const stockUnspiderfy = proto._animationUnspiderfy

  proto._animationSpiderfy = function (
    this: InternalCluster,
    childMarkers: SpiderMarker[],
    positions: L.Point[],
  ) {
    const group = this._group
    const map = group._map
    const fg = group._featureGroup
    const legOptions = L.extend({}, group.options.spiderLegPolylineOptions) as L.PolylineOptions

    group._ignoreMove = true
    // hide the rest of the map's pins for the whole animation, not just once
    // the plugin's `spiderfied` event lands (styled.ts keys off `.spiderfying`)
    map.getContainer().classList.add('spiderfying')
    // the badge we clicked becomes a close button straight away (styled.ts turns
    // `.marker-cluster.spiderfy-visible` into an ✕); MotifClusterLayer wires the click
    this.getElement()?.classList.add('spiderfy-visible')

    // generateSpiral fills index 0 = outermost .. last index = innermost, so a
    // delay of `(lastIndex - i)` steps makes the innermost pin pop in first.
    const lastIndex = childMarkers.length - 1
    const stagger = effectiveStagger(childMarkers.length)

    // 1. place every pin straight on its final slot, inner image collapsed + hidden
    const inners = childMarkers.map((marker, i) => {
      const target = map.layerPointToLatLng(positions[i])

      const leg = new L.Polyline([this._latlng, target], legOptions)
      map.addLayer(leg)
      marker._spiderLeg = leg

      marker.setZIndexOffset?.(1_000_000)
      fg.addLayer(marker)
      marker._preSpiderfyLatlng = marker.getLatLng()
      marker.setLatLng(target)

      marker.getElement()?.classList.add('spiderfy-visible')

      const inner = innerImage(marker)
      if (inner) {
        inner.style.transition = 'none'
        inner.style.transform = `scale(${SPIDERFY.startScale})`
        inner.style.opacity = '0'
      }
      return inner
    })

    // 2. commit that collapsed start state
    group._forceLayout()
    group._animationStart()

    // 3. reveal each pin in place, innermost → outermost
    inners.forEach((inner, i) => {
      if (!inner) return
      inner.style.transition = `transform ${SPIDERFY.transitionMs}ms ease-out, opacity ${SPIDERFY.transitionMs}ms ease-out`
      inner.style.transitionDelay = `${(lastIndex - i) * stagger}ms`
      inner.style.transform = 'scale(1)'
      inner.style.opacity = '1'
    })
    group._ignoreMove = false

    // 4. finish once the last, most-delayed pin has appeared
    const totalMs = lastIndex * stagger + SPIDERFY.transitionMs + TRANSITION_TAIL_MS
    setTimeout(() => {
      inners.forEach(clearInlineReveal)
      group._animationEnd()
      if (group._spiderfied === this) {
        group.fire('spiderfied', { cluster: this, markers: childMarkers })
      }
    }, totalMs)
  }

  proto._animationUnspiderfy = function (this: InternalCluster, zoomDetails?: ZoomAnimDetails) {
    // A zoom-driven collapse has to be quick: the zoom transition and the
    // re-cluster that follows both wait on it. Only the user-initiated close
    // (map click / dismiss) gets the staggered scale-out.
    if (zoomDetails) {
      stockUnspiderfy.call(this, zoomDetails)
      return
    }

    const group = this._group
    const map = group._map
    const fg = group._featureGroup
    const childMarkers = this.getAllChildMarkers(null, true)

    group._ignoreMove = true
    group._animationStart()
    this.setOpacity(1)
    // revert the close button, and let the rest of the map's pins start fading
    // back in now (on click) rather than when the collapse cascade finishes.
    // `unspiderfied` still fires at the end for the DOM/layer teardown.
    this.getElement()?.classList.remove('spiderfy-visible')
    map.getContainer().classList.remove('spiderfying')

    const lastIndex = childMarkers.length - 1
    const stagger = effectiveStagger(childMarkers.length)

    // exact reverse of the reveal: outermost (index 0) collapses first
    const inners = childMarkers.map((marker, i) => {
      if (!marker._preSpiderfyLatlng) return null
      marker.closePopup()

      const inner = innerImage(marker)
      if (inner) {
        inner.style.transition = `transform ${SPIDERFY.transitionMs}ms ease-in, opacity ${SPIDERFY.transitionMs}ms ease-in`
        inner.style.transitionDelay = `${i * stagger}ms`
        inner.style.transform = `scale(${SPIDERFY.startScale})`
        inner.style.opacity = '0'
      }

      // Put `_latlng` back to the real point now, while moves are ignored, so
      // re-clustering is correct — but re-pin the pixel position so the pin
      // fades out where it sits instead of jumping to the real point first.
      const spiderPos = map.latLngToLayerPoint(marker.getLatLng())
      marker.setLatLng(marker._preSpiderfyLatlng)
      delete marker._preSpiderfyLatlng
      marker._setPos?.(spiderPos)

      return inner
    })

    group._ignoreMove = false

    const totalMs = lastIndex * stagger + SPIDERFY.transitionMs + TRANSITION_TAIL_MS
    setTimeout(() => {
      const stillSpidered = childMarkers.filter(marker => marker._spiderLeg)

      childMarkers.forEach((marker, i) => {
        if (!marker._spiderLeg) return

        clearInlineReveal(inners[i])
        marker.setZIndexOffset?.(0)

        // a lone leftover child stays on the map as its own pin (plugin parity)
        if (stillSpidered.length > 1) {
          fg.removeLayer(marker)
        }
        map.removeLayer(marker._spiderLeg)
        delete marker._spiderLeg
      })

      group._animationEnd()
      group.fire('unspiderfied', { cluster: this, markers: childMarkers })
    }, totalMs)
  }
}
